import { InvoiceDetailDoc } from '@gooduncles/gu-app-schema';
import { Tag } from 'antd';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef, ColGroupDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { FC, useMemo } from 'react';
import { isProduction } from 'src/constants';

import { formatDateForAgCell, formatNumberForAgCell, onCellValueChangedWithUpdate } from 'src/utils/ag-grid-util';
import { encryptInvoiceId } from 'src/utils/payment-util';

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import AgCellSyncMessageResultButton from 'src/atomic-components/atoms/AgCell/AgCellSyncMessageResultButton';
import KakaotalkContentPopover from 'src/components/AlimtalkMessage/AlimtalkContentPopover/AlimtalkContentPopover';

const onCellValueChanged = onCellValueChangedWithUpdate<InvoiceDetailDoc>('청구서 고객용');

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

interface InvoiceDetailTableProps {
  rowData: InvoiceDetailDoc[];
  setSelectedRows: React.Dispatch<React.SetStateAction<InvoiceDetailDoc[]>>;
  /** storeId → v2 전환(푸시 가능) 여부 */
  conversionMap: Record<string, boolean>;
}

const InvoiceDetailTable: FC<InvoiceDetailTableProps> = ({ rowData, setSelectedRows, conversionMap }) => {
  const columnDefs: (ColDef<InvoiceDetailDoc> | ColGroupDef<InvoiceDetailDoc>)[] = useMemo(
    () => [
      { field: '_id', hide: true },
      { headerName: '매장Id', field: 'storeId', hide: true },
      {
        headerName: '청구대상',
        field: 'recipientInfo.name',
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
      },
      {
        headerName: '전환상태',
        width: 110,
        valueGetter: ({ data }) => (conversionMap[(data as InvoiceDetailDoc).storeId] ? '푸시 가능' : '알림톡만'),
        cellRenderer: ({ value }: { value: string }) =>
          value === '푸시 가능' ? <Tag color='green'>푸시 가능</Tag> : <Tag color='orange'>알림톡만</Tag>,
      },
      {
        headerName: '생성시각',
        field: '_timeCreate',
        valueFormatter: formatDateForAgCell('yyyy-MM-dd HH:mm:ss'),
      },
      { headerName: '사업자등록번호', field: 'recipientInfo.businessNumber', hide: true },
      {
        headerName: '수신번호',
        field: 'recipientInfo.userTel',
        editable: true,
        onCellValueChanged: (params) => onCellValueChanged(params, 'invoiceDetail'),
      },
      {
        headerName: '미납금',
        field: 'prevSettlementResult.unpaidAmount',
        valueFormatter: formatNumberForAgCell,
        width: 120,
        type: 'numericColumn',
      },
      {
        headerName: '청구금액',
        field: 'invoiceAmount',
        valueFormatter: formatNumberForAgCell,
        width: 120,
        type: 'numericColumn',
      },
      {
        headerName: '면세',
        field: 'taxExemptAmount',
        valueFormatter: formatNumberForAgCell,
        width: 120,
        type: 'numericColumn',
        hide: true,
      },
      {
        headerName: '과세',
        field: 'taxableAmount',
        valueFormatter: formatNumberForAgCell,
        width: 120,
        type: 'numericColumn',
        hide: true,
      },
      {
        headerName: '요청결과',
        width: 120,
        valueGetter: ({ data }) => {
          const { alimtalkMessage } = data as InvoiceDetailDoc;
          if (alimtalkMessage?.result?.requestResult.result) {
            return alimtalkMessage.result.requestResult.result === 'success'
              ? '🟢 성공'
              : `❌ ${alimtalkMessage.result.requestResult.error}`;
          }
          return null;
        },
      },
      {
        headerName: '발송결과',
        width: 120,
        valueGetter: ({ data }) => {
          const { alimtalkMessage } = data as InvoiceDetailDoc;
          if (alimtalkMessage?.result?.messageResult?.resultCode) {
            return alimtalkMessage.result.messageResult.resultCode === '1000'
              ? '🟢 성공'
              : `❌ ${alimtalkMessage.result.messageResult.error}`;
          }
          return null;
        },
        cellRenderer: (props: any) => {
          const { value, data } = props;
          if (value) return value;
          const { alimtalkMessage } = data as InvoiceDetailDoc;
          if (alimtalkMessage) {
            const { alimtalkMessageDocId, requestId, recipientSeq, result } = alimtalkMessage;
            if (result?.messageResult) {
              return (
                <AgCellSyncMessageResultButton
                  type='alimtalk'
                  messageDocId={alimtalkMessageDocId}
                  requestId={requestId}
                  recipientSeq={recipientSeq}
                />
              );
            }
          }
          return '⚠️ 요청 결과 없음';
        },
      },
      {
        headerName: '발송내용',
        field: 'alimtalkMessage.result.messageResult.content',
        cellRenderer: KakaotalkContentPopover,
        cellRendererParams: (params: any) => {
          const { alimtalkMessage } = params.data as InvoiceDetailDoc;
          if (alimtalkMessage?.result?.messageResult?.buttons) {
            return {
              buttons: alimtalkMessage.result.messageResult.buttons,
            };
          }
        },
      },
      {
        headerName: '메시지',
        field: 'message',
        editable: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorParams: {
          maxLength: 3000,
        },
        cellEditorPopup: true,
        onCellValueChanged: (params) => onCellValueChanged(params, 'invoiceDetail'),
      },
      {
        headerName: '바로가기',
        field: '_id',
        width: 140,
        cellRenderer: (params: any) => {
          const data = params.data as InvoiceDetailDoc;
          const encryptedInvoiceId = encryptInvoiceId(data._id);
          const base = isProduction
            ? 'https://commerce-gooduncles.web.app/invoice'
            : 'https://commerce-gooduncles-dev.web.app/invoice';
          return (
            <a href={`${base}/${encryptedInvoiceId}`} target='_blank' rel='noreferrer'>
              링크
            </a>
          );
        },
      },
      {
        headerName: '액션',
        minWidth: 120,
        cellRenderer: DownloadRawDataCell,
      },
    ],
    [conversionMap],
  );

  return (
    <div className='ag-theme-alpine height100'>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        // 행 선택을 허용한다.
        onSelectionChanged={(e) => setSelectedRows(e.api.getSelectedRows())}
        rowSelection={'multiple'}
      />
    </div>
  );
};

export default InvoiceDetailTable;
